import { useEffect, useState } from 'react';
import { supabase } from '@/shared/lib/supabase';
import type { HostingSubscription } from '../types/hostingSubscription.types';

/**
 * useHostingSubscriptions — reads the signed-in customer's hosting subscriptions.
 *
 * RLS-scoped (`customer_id = auth.uid()`). Returns ALL rows (pending / active /
 * at_risk / cancelled) so the UI can show history; consumers filter for the
 * active row themselves.
 *
 * Spec: _bmad-output/PHASE_PF_Q3_REVERSAL_BRD_2026-06-16.md §7.1
 */

interface HostingSubscriptionRow {
  id: string;
  customer_link_id: string;
  customer_id: string;
  service_plan_id: string;
  status: HostingSubscription['status'];
  recurring_amount: string | number;
  next_billing_date: string;
  last_cycle_at: string | null;
  failed_cycle_at: string | null;
  cancelled_at: string | null;
  cancel_reason: HostingSubscription['cancelReason'];
  started_at: string;
}

function mapRow(row: HostingSubscriptionRow): HostingSubscription {
  return {
    id: row.id,
    customerLinkId: row.customer_link_id,
    customerId: row.customer_id,
    servicePlanId: row.service_plan_id,
    status: row.status,
    recurringAmount: Number(row.recurring_amount),
    nextBillingDate: row.next_billing_date,
    lastCycleAt: row.last_cycle_at,
    failedCycleAt: row.failed_cycle_at,
    cancelledAt: row.cancelled_at,
    cancelReason: row.cancel_reason,
    startedAt: row.started_at,
  };
}

export interface UseHostingSubscriptionsResult {
  subscriptions: HostingSubscription[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useHostingSubscriptions(): UseHostingSubscriptionsResult {
  const [subscriptions, setSubscriptions] = useState<HostingSubscription[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function fetchAll() {
    setError(null);
    const { data, error: queryError } = await supabase
      .from('hosting_subscription')
      .select(
        'id, customer_link_id, customer_id, service_plan_id, status, recurring_amount, next_billing_date, last_cycle_at, failed_cycle_at, cancelled_at, cancel_reason, started_at'
      )
      .order('started_at', { ascending: false });

    if (queryError) {
      setError(queryError.message);
      setSubscriptions([]);
    } else {
      setSubscriptions((data ?? []).map(mapRow));
    }
    setIsLoading(false);
  }

  useEffect(() => {
    fetchAll();
  }, []);

  return {
    subscriptions,
    isLoading,
    error,
    refresh: fetchAll,
  };
}
